<template lang="pug">
  .checklist(v-if="editable || this.items && this.items.length")
    .header
      template(v-if="this.items && this.items.length")
        .title Чек-лист
        BaseProgressbar(:value="progress").progress-bar
        .progress-string Выполнено: {{ itemsChecked.length }} из {{ items.length }}
        BaseSwitch.edit-switch(v-model="editing" v-if="editable") Редактировать
        button.button.toggle-list(@click="toggleFolding")
          | {{ isExpanded ? 'Свернуть' : 'Развернуть' }}
          Icon(name="angle-down" pointer :class="{ expanded: isExpanded }")
      template(v-else)
        button.button(@click="onAddItem")
          Icon(name="add" pointer)
          | Добавить чек-лист
    .body(v-if="this.items && this.items.length" v-show="isExpanded")
      CheckListItems(
        :items="items"
        :editable="editable && editing"
        :editableItem="editableItem"
        :changeable="changeable"
        @change="onChange"
        @edit="editableItem = $event"
        @enter="onEnter"
      )
      .footer(v-if="editable && editing")
        button.button.bordered(@click="onAddItem()")
          Icon(name="add" pointer)
          | Добавить пункт
        button.button.bordered(@click="toggleNumbering")
          Icon(name="list-ol2" pointer)
          | {{ isNumerated ? 'Сбросить нумерацию' : 'Добавить нумерацию' }}
        button.button.bordered(@click="onDeleteList")
          Icon(name="trash" pointer)
          | Удалить чек-лист
</template>

<script>
import modelCustomSettings from '@/model/userCustomSettings';

import BaseProgressbar from '@/components/BaseProgressbar.vue';
import CheckListItems from '@/components/checlkist/CheckListItems.vue';
import BaseSwitch from '@/components/BaseSwitch.vue';

export default {
  name: 'CheckList',
  components: { BaseSwitch, BaseProgressbar, CheckListItems },
  model: { prop: 'value', event: 'change' },
  props: {
    value: Array,
    editable: Boolean,
    changeable: Boolean,
    id: Number,
  },
  data() {
    return {
      items: [],
      editing: true,
      editableItem: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.items = val ?? [];
      },
    },
  },
  computed: {
    itemsChecked() { return this.items.filter((i) => i.checked); },
    progress() { return (this.itemsChecked.length / this.items.length).toFixed(2) * 100; },
    isNumerated() { return !!this.items?.[0]?.number; },
    currUser() { return this.$store.state.auth.user; },
    foldedItems() { return this.currUser?.customSettings?.foldedTaskChecklists ?? []; },
    isExpanded() { return !this.foldedItems.includes(this.id); },
  },
  methods: {
    toggleFolding() {
      const foldedTaskChecklists = this.isExpanded
        ? [...this.foldedItems, this.id]
        : this.foldedItems.filter(i => i !== this.id);
      modelCustomSettings.set({ foldedTaskChecklists });
    },
    toggleNumbering() {
      const items = this.isNumerated
        ? this.items.map(i => ({ label: i.label, checked: i.checked }))
        : this.items.map((v, i) => ({ ...v, number: i + 1 }));

      this.onChange({ items, save: true });
    },

    onEnter(item) {
      setTimeout(() => {
        const index = this.items.indexOf(item) + 1;

        if (index) {
          this.onAddItem(index);
        }
      });
    },

    onAddItem(toIndex) {
      const newItem = this.isNumerated
        ? { checked: false, label: '', number: this.items.reduce((a, v) => Math.max(a, v.number), 0) + 1 }
        : { checked: false, label: '' };

      if (toIndex !== undefined) {
        this.items.splice(toIndex, 0, newItem);
      } else {
        this.items.push(newItem);
      }

      this.editableItem = newItem;
    },

    onChange({ items, save }) {
      this.editableItem = null;
      this.items = items;

      if (!save) return;

      this.$emit('change', this.items.filter((i) => i.label.trim().length > 0));
    },

    async onDeleteList() {
      if (await this.$confirm('Вы действительно хотите удалить все пункты чек-листа?')) {
        this.$emit('change', []);
      }
    },
  },
};
</script>

<style scoped lang="scss">
  .checklist {
    display: flex;
    flex-direction: column;
    padding: 30px 16px;
    gap: 30px;
    background: #F5F7F8;
    border-radius: 6px;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
  }

  .title {
    color: rgba(30, 49, 71, 1);
  }

  .progress-bar {
    display: block;
    flex-grow: 1;
    max-width: 350px;
  }

  .progress-string {
    font-weight: 500;
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--color-middle-gray);
  }

  .toggle-list {
    margin-left: auto;

    .expanded {
      transform: rotate(180deg);
    }
  }

  .footer {
    display: flex;
    gap: 15px;
    justify-content: space-between;
    align-items: center;
    padding-top: 15px;

    border-top: 1px solid #D9DADA;
  }

  .button {
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--color-middle-gray);
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;

    &.bordered {
      border: 1px solid #C4C4C4;
      border-radius: 6px;
      padding: 8px 20px;
    }

    @media (max-width: 768px) {
      &.bordered {
        border: none;
        padding: 8px;
        flex-direction: column;
      }
    }

    @media (max-width: 568px) {
      &.bordered {
        font-size: 10px;
        line-height: 1.2;
      }
    }
  }

  @media (max-width: 800px) {
    .header {
      display: grid;
      grid-template-columns: 1fr max-content;
    }

    .edit-switch {
      grid-column: 2;
      grid-row: 2;
      justify-self: end;
    }

    .progress-string {
      grid-column: 1;
      grid-row: 2;
    }

    .progress-bar {
      grid-column: 1 / 2 span;
      grid-row: 3;
      max-width: 100%;
    }
  }
</style>
